import { useCallback, useEffect, useState } from "react"
import { Image, Platform, Pressable, StyleSheet, Text, View } from "react-native"

import { useChannelLink } from "src/context/ChannelLinkContext"

const APP_ICON = require("../../assets/images/app-icon-all.png")

const IOS_APP_STORE_URL = "https://apps.apple.com/us/app/sidekick-platform/id6504539500"
const ANDROID_PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.sidekickwellness.chat"
const BANNER_DISMISSED_KEY = "sk_app_banner_dismissed"
const APP_OPEN_TIMEOUT_MS = 2000

function getMobileOS(): "ios" | "android" | null {
  if (Platform.OS !== "web" || typeof window === "undefined") return null
  const ua = window.navigator?.userAgent ?? ""
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios"
  if (/Android/i.test(ua)) return "android"
  return null
}

/**
 * A "Smart App Banner" style strip shown at the top of the page on mobile browsers.
 * Provides a native-feeling prompt to open or download the Sidekick Platform app.
 *
 * - Tapping "Open" attempts to launch the app via its custom URL scheme. If the app
 *   is not installed the user is redirected to the appropriate store after a short delay.
 * - Tapping ✕ dismisses the banner for the current browser session.
 */
export function MobileAppBanner() {
  const { pendingChannelId } = useChannelLink()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (!getMobileOS()) return
    try {
      if (sessionStorage.getItem(BANNER_DISMISSED_KEY)) return
    } catch {
      // sessionStorage unavailable — show the banner anyway
    }
    setVisible(true)
  }, [])

  const handleDismiss = useCallback(() => {
    try {
      sessionStorage.setItem(BANNER_DISMISSED_KEY, "1")
    } catch {
      // ignore
    }
    setVisible(false)
  }, [])

  const handleOpen = useCallback(() => {
    const os = getMobileOS()
    if (!os) return

    const storeUrl = os === "ios" ? IOS_APP_STORE_URL : ANDROID_PLAY_STORE_URL
    const deeplink = pendingChannelId
      ? `sidekick://?channel=${encodeURIComponent(pendingChannelId)}`
      : "sidekick://"

    // Detect whether the app opened by watching for the page becoming hidden.
    // If still visible after the timeout the app is not installed → go to the store.
    let appOpened = false
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") appOpened = true
    }
    document.addEventListener("visibilitychange", onVisibilityChange)
    window.location.href = deeplink
    setTimeout(() => {
      document.removeEventListener("visibilitychange", onVisibilityChange)
      if (!appOpened) window.location.href = storeUrl
    }, APP_OPEN_TIMEOUT_MS)
  }, [pendingChannelId])

  if (!visible) return null

  const os = getMobileOS()
  const storeLabel = os === "ios" ? "Free on the App Store" : "Free on Google Play"

  return (
    <View style={styles.banner}>
      <Pressable onPress={handleDismiss} style={styles.closeBtn} hitSlop={8}>
        <Text style={styles.closeText}>✕</Text>
      </Pressable>
      <Image source={APP_ICON} style={styles.icon} />
      <View style={styles.textBlock}>
        <Text style={styles.title} numberOfLines={1}>
          {"Sidekick Platform"}
        </Text>
        <Text style={styles.subtitle} numberOfLines={1}>
          {storeLabel}
        </Text>
      </View>
      <Pressable onPress={handleOpen} style={styles.openBtn}>
        <Text style={styles.openText}>{"Open"}</Text>
      </Pressable>
    </View>
  )
}

const styles = StyleSheet.create({
  banner: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    position: "fixed" as any,
    top: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#1C1C1E",
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "#3A3A3C",
  },
  closeBtn: {
    padding: 4,
  },
  closeText: {
    color: "#8E8E93",
    fontSize: 14,
  },
  icon: {
    width: 44,
    height: 44,
    borderRadius: 10,
  },
  textBlock: {
    flex: 1,
  },
  title: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "600",
  },
  subtitle: {
    color: "#8E8E93",
    fontSize: 12,
    marginTop: 2,
  },
  openBtn: {
    backgroundColor: "#0A84FF",
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: 14,
  },
  openText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "600",
  },
})
